import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { TableCell, TableRow } from '@mui/material';

import { updateAppointmentSlotSearch } from '../../../store/calendarService';
import {
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import { loadParticipant } from '../../../store/participantService';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import { hasRole } from '../../../utils/userRoles';
import ConfirmPrompt from '../../ui/notices/confirmPrompt/ConfirmPrompt';

import table from '../../../commonStyles/tables.module.css';

const AppointmentSlotsTableRow = ({ row, acceptedRoles, newParticipantId }) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [showConfirmPrompt, setShowConfirmPrompt] = useState(false);
    const [isFull, setIsFull] = useState(false);
    const [isAppointmentScheduled, setIsAppointmentScheduled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        setIsFull(row.numberOfSlots === row.participantIds.length);
    }, [row]);

    useEffect(() => {
        if (successMessage.includes(`Appointment slot ${row.type} has been updated`)) {
            setIsAppointmentScheduled(true);
            if (currentParticipant.id !== newParticipantId)
                dispatch(loadParticipant(newParticipantId));
        }
    }, [successMessage]);

    useEffect(() => {
        if (
            Object.keys(currentParticipant)?.length > 0 &&
            currentParticipant.id === newParticipantId &&
            isAppointmentScheduled
        ) {
            if (!(currentParticipant.contractId in contractDetailsById))
                dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
            if (!(currentParticipant.contractId in servicesByContractId))
                dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
        }
    }, [currentParticipant, isAppointmentScheduled]);

    useEffect(() => {
        if (
            isAppointmentScheduled &&
            currentParticipant?.id === newParticipantId &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        )
            navigate('/edit_participant');
    }, [isAppointmentScheduled, contractDetailsById, servicesByContractId, currentParticipant]);

    // EVENT HANDLERS
    const onDateClick = () => setShowConfirmPrompt(true);

    const onConfirmPromptCancel = () => setShowConfirmPrompt(false);

    const onConfirmPromptConfirm = () => {
        setShowConfirmPrompt(false);
        /* eslint-disable no-unused-vars */
        const { time, adviser, ...rest } = row;
        const payload = { ...rest, participantIds: [...row.participantIds, newParticipantId] }; // add PT ID
        dispatch(updateAppointmentSlotSearch(row.type, payload));
    };

    // RENDER
    return (
        <>
            <TableRow key={row.id} hover className={`${table.basicRowStripes}`}>
                <TableCell
                    onClick={!isFull ? onDateClick : null}
                    disabled={!hasRole(acceptedRoles, roles)}>
                    <span className={!isFull ? table.rowLink : ''}>
                        {reverseFormatDate(row.date)}
                    </span>
                </TableCell>
                <TableCell>{row.time}</TableCell>
                <TableCell>{row.location}</TableCell>
                <TableCell>{row.adviser}</TableCell>
            </TableRow>
            {showConfirmPrompt && (
                <ConfirmPrompt
                    isOpen={showConfirmPrompt}
                    onCancel={onConfirmPromptCancel}
                    onConfirm={onConfirmPromptConfirm}
                    content="Do you want to Submit your Participant to this Slot?"
                />
            )}
        </>
    );
};

AppointmentSlotsTableRow.propTypes = {
    row: PropTypes.object,
    acceptedRoles: PropTypes.arrayOf(PropTypes.string),
    newParticipantId: PropTypes.string
};

export default AppointmentSlotsTableRow;
